/**
 * OptimizationRecommendationViewer - AI-powered optimization recommendations with life-critical controls
 * 
 * Features:
 * - AI-generated recommendation cards with safety validation
 * - Implementation impact simulator with ROI calculations  
 * - Multi-level approval workflow for life-critical changes
 * - Real-time safety and compliance monitoring
 * - Emergency override capabilities for crisis situations
 * - Comprehensive regulatory compliance tracking
 * 
 * Life-Critical Requirements:
 * - Zero tolerance for unsafe recommendations (safety score < 70)
 * - Mandatory multi-level approval for high-risk changes
 * - Immutable audit trails for all actions
 * - Emergency break-glass procedures
 * - Real-time regulatory compliance monitoring
 */

import {
    Accordion,
    ActionIcon,
    Alert, Badge, Button, Card,
    Center,
    Checkbox,
    Container,
    Divider,
    Grid, Group,
    Modal,
    NumberInput,
    Paper, Progress,
    RingProgress,
    Select,
    SimpleGrid,
    Stack,
    Table,
    Tabs,
    Text,
    Textarea,
    ThemeIcon,
    Title,
    Tooltip
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
    IconAdjustments,
    IconAlertTriangle,
    IconAnalyze,
    IconBrain,
    IconCertificate,
    IconChartBar,
    IconCheck,
    IconClock, IconCurrencyDollar,
    IconDownload,
    IconEmergencyBed,
    IconEye,
    IconGavel,
    IconInfoCircle,
    IconPlayerPlay,
    IconReportAnalytics,
    IconRobot,
    IconScale,
    IconSettings,
    IconShield,
    IconShieldCheck,
    IconShieldX,
    IconTrendingUp,
    IconX
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useMetadataRepository from './hooks/useMetadataRepository';
import { OptimizationRecommendation, OptimizationUtils, SAFETY_THRESHOLDS } from './types-16-complete';

// Status constants (matches generated GraphQL enums)
const RecommendationStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  IMPLEMENTED: 'IMPLEMENTED',
  MONITORING: 'MONITORING',
} as const;

interface OptimizationRecommendationViewerProps {
    mode: 'view' | 'analyze' | 'approve' | 'implement' | 'history' | 'generate';
    workflowId: string;
    workflowName?: string;
    tenant?: string;
    recommendations?: OptimizationRecommendation[];
    onGenerate?: (request: any) => Promise<OptimizationRecommendation[]>;
    onGenerateNew?: (options: GenerationOptions) => Promise<OptimizationRecommendation[]>;
    onApprove?: (id: string, approved: boolean, notes?: string) => Promise<void>;
    onImplement?: (id: string, implementation_plan: ImplementationPlan) => Promise<void>;
    onReject?: (id: string, reason: string) => Promise<void>;
    onSimulate?: (recommendation: any) => Promise<any>;
    onSimulateImpact?: (id: string) => Promise<ImpactSimulation>;
    onExportRecommendations?: (format: 'csv' | 'json' | 'pdf') => Promise<void>;
    onExport?: (format: any, recommendations: any) => Promise<void>;
    onMonitorImplementation?: (id: string) => Promise<ImplementationProgress>;
    onRefresh?: () => Promise<any[]>;
    userRole?: 'viewer' | 'approver' | 'implementer' | 'safety_officer' | 'compliance_manager' | 'admin';
    permissions?: string[];
    lifeCriticalMode?: boolean;
}

interface GenerationOptions {
    include_performance: boolean;
    include_cost: boolean;
    include_reliability: boolean;
    include_compliance: boolean;
    include_safety: boolean;
    analysis_depth: 'SHALLOW' | 'STANDARD' | 'DEEP' | 'COMPREHENSIVE';
    max_recommendations: number;
    focus_areas?: string[];
    risk_tolerance: 'CONSERVATIVE' | 'MODERATE' | 'AGGRESSIVE';
}

interface ImplementationPlan {
    phases: ImplementationPhase[];
    rollback_plan: string;
    testing_strategy: string;
    monitoring_plan: string;
    estimated_duration: number; // hours
    risk_mitigation: string[];
    approval_chain: string[];
}

interface ImplementationPhase {
    name: string;
    description: string;
    estimated_duration: number; // hours
    dependencies: string[];
    rollback_steps: string[];
    validation_criteria: string[];
}

interface ImpactSimulation {
    recommendation_id: string;
    execution_time_improvement: number; // percentage
    error_rate_improvement: number; // percentage  
    throughput_improvement: number; // percentage
    annual_savings: number; // USD
    implementation_cost: number; // USD
    roi_months: number;
    roi_percentage: number;
    confidence_interval: {
        lower: number;
        upper: number;
        confidence: number;
    };
    risk_factors: RiskFactor[];
}

interface RiskFactor {
    category: string;
    description: string;
    probability: number; // 0-1
    impact: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    mitigation: string;
}

interface ImplementationProgress {
    recommendation_id: string;
    current_phase: string;
    overall_progress: number; // 0-1
    estimated_completion: string;
    issues: ImplementationIssue[];
    metrics_snapshot: {
        timestamp: string;
        execution_time: number;
        error_rate: number;
        throughput: number;
        resource_utilization: number;
    };
}

interface ImplementationIssue {
    severity: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
    category: string;
    description: string;
    resolution?: string;
    created_at: string;
    resolved_at?: string;
}

export const OptimizationRecommendationViewer: React.FC<OptimizationRecommendationViewerProps> = ({
    mode,
    workflowId,
    workflowName = 'Unknown Workflow',
    tenant = 'default',
    recommendations = [],
    onGenerateNew,
    onApprove,
    onImplement,
    onReject,
    onSimulateImpact,
    onExportRecommendations,
    onMonitorImplementation,
    userRole = 'viewer',
    permissions = [],
    lifeCriticalMode = true
}) => {
    // State management for complex life-critical operations
    const [activeTab, setActiveTab] = useState<string>('recommendations');
    const [selectedRecommendation, setSelectedRecommendation] = useState<OptimizationRecommendation | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [simulationResults, setSimulationResults] = useState<Map<string, ImpactSimulation>>(new Map());
    const [implementationProgress, setImplementationProgress] = useState<Map<string, ImplementationProgress>>(new Map());
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showImplementationModal, setShowImplementationModal] = useState(false);
    const [showGenerationModal, setShowGenerationModal] = useState(false);
    const [approvalNotes, setApprovalNotes] = useState('');
    const [filterSeverity, setFilterSeverity] = useState<string | null>(null);
    const [filterType, setFilterType] = useState<string | null>(null);
    const [filterStatus, setFilterStatus] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<'priority' | 'safety' | 'savings' | 'created'>('priority');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [generationOptions, setGenerationOptions] = useState<GenerationOptions>({
        include_performance: true,
        include_cost: true,
        include_reliability: true,
        include_compliance: true,
        include_safety: true,
        analysis_depth: 'STANDARD',
        max_recommendations: 5,
        risk_tolerance: 'MODERATE'
    });

    // Repository-driven compliance thresholds (tenant-scoped)
    const { getComplianceThresholds, getSafetyThresholds } = useMetadataRepository();
    const [complianceThresholds, setComplianceThresholds] = useState<{ excellent: number; good: number; warning: number; critical: number }>({ excellent: 0, good: 0, warning: 0, critical: 0 });
    const [safetyThresholds, setSafetyThresholds] = useState<{ CRITICAL: number; WARNING: number; EXCELLENT: number; EMERGENCY: number }>({ CRITICAL: 0, WARNING: 0, EXCELLENT: 0, EMERGENCY: 0 });

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const t = await getComplianceThresholds(tenant || 'default');
                if (mounted && t) {
                    setComplianceThresholds(prev =>
                        prev && prev.excellent === t.excellent && prev.good === t.good && prev.warning === t.warning && prev.critical === t.critical
                            ? prev
                            : t
                    );
                }
            } catch (_: any) {}
            try {
                const s = await getSafetyThresholds(tenant || 'default');
                if (mounted && s) {
                    setSafetyThresholds(prev =>
                        prev && prev.CRITICAL === s.CRITICAL && prev.WARNING === s.WARNING && prev.EXCELLENT === s.EXCELLENT && prev.EMERGENCY === s.EMERGENCY
                            ? prev
                            : s
                    );
                }
            } catch (_: any) {}
        })();
        return () => { mounted = false; };
    }, [tenant, getComplianceThresholds, getSafetyThresholds]);

    // Life-critical safety calculations
    const safetyMetrics = useMemo(() => {
        const total = recommendations.length;
        const safe = recommendations.filter(r => OptimizationUtils.isSafeToImplement(r)).length;
        const blocked = recommendations.filter(r => r.safety_validation.safety_score < SAFETY_THRESHOLDS.MINIMUM_SAFETY_SCORE).length;
        const highRisk = recommendations.filter(r => r.regulatory_impact.compliance_risk_score >= complianceThresholds.critical).length;
        const pending = recommendations.filter(r => r.status === RecommendationStatus.PENDING).length;
        const implemented = recommendations.filter(r => r.status === RecommendationStatus.IMPLEMENTED).length;

        const totalSavings = recommendations
            .filter(r => r.status === RecommendationStatus.IMPLEMENTED)
            .reduce((sum, r) => sum + r.estimated_impact.cost_impact.annual_savings_usd, 0);

        const averageSafetyScore = total > 0 ? 
            recommendations.reduce((sum, r) => sum + r.safety_validation.safety_score, 0) / total : 0;

        return {
            total,
            safe,
            blocked,
            highRisk,
            pending,
            implemented,
            totalSavings,
            averageSafetyScore,
            safetyRate: total > 0 ? (safe / total) * 100 : 0,
            implementationRate: total > 0 ? (implemented / total) * 100 : 0,
            blockageRate: total > 0 ? (blocked / total) * 100 : 0
        };
    }, [recommendations, complianceThresholds]);

    // Filter and sort recommendations based on user selections
    const filteredAndSortedRecommendations = useMemo(() => {
        let filtered = [...recommendations];

        // Apply filters
        if (filterSeverity) {
            filtered = filtered.filter(r => r.severity === filterSeverity);
        }
        if (filterType) {
            filtered = filtered.filter(r => r.recommendation_type === filterType);
        }
        if (filterStatus) {
            filtered = filtered.filter(r => r.status === filterStatus);
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aVal: number, bVal: number;
            
            switch (sortBy) {
                case 'priority':
                    aVal = OptimizationUtils.calculatePriority(a);
                    bVal = OptimizationUtils.calculatePriority(b);
                    break;
                case 'safety':
                    aVal = a.safety_validation.safety_score;
                    bVal = b.safety_validation.safety_score;
                    break;
                case 'savings':
                    aVal = a.estimated_impact.cost_impact.annual_savings_usd;
                    bVal = b.estimated_impact.cost_impact.annual_savings_usd;
                    break;
                case 'created':
                    aVal = new Date(a.created_at).getTime();
                    bVal = new Date(b.created_at).getTime();
                    break;
                default:
                    aVal = a.priority_score;
                    bVal = b.priority_score;
            }

            return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
        });

        return filtered;
    }, [recommendations, filterSeverity, filterType, filterStatus, sortBy, sortOrder]);

    // Generate new AI recommendations with comprehensive options
    const handleGenerateRecommendations = useCallback(async (options: GenerationOptions) => {
        setIsGenerating(true);
        try {
            const newRecommendations = await onGenerateNew?.(options);
            notifications.show({
                title: '🤖 AI Analysis Complete',
                message: `Generated ${newRecommendations?.length || 0} new optimization recommendations`,
                color: 'blue',
                icon: <IconRobot size={20} />
            });
        } catch (error) {
            notifications.show({
                title: '⚠️ Generation Failed',
                message: `Failed to generate recommendations: ${error}`,
                color: 'red',
                icon: <IconAlertTriangle size={20} />
            });
        } finally {
            setIsGenerating(false);
            setShowGenerationModal(false);
        }
    }, [onGenerateNew]);

    // Simulate implementation impact with comprehensive analysis
    const handleSimulateImpact = useCallback(async (recommendation: OptimizationRecommendation) => {
        try {
            const simulation = await onSimulateImpact?.(recommendation.id);
            if (simulation) {
                setSimulationResults(prev => new Map(prev).set(recommendation.id, simulation));
                
                notifications.show({
                    title: '📊 Impact Simulation Complete',
                    message: `ROI: ${simulation.roi_percentage}% (${simulation.roi_months} months)`,
                    color: 'blue',
                    icon: <IconAnalyze size={20} />
                });
            }
        } catch (error) {
            notifications.show({
                title: '❌ Simulation Failed',
                message: `Failed to simulate impact: ${error}`,
                color: 'red'
            });
        }
    }, [onSimulateImpact]);

    // Handle life-critical approval workflow
    const handleApproval = useCallback(async (recommendation: OptimizationRecommendation, approved: boolean) => {
        try {
            // Life-critical validation checks
            if (approved && recommendation.safety_validation.safety_score < SAFETY_THRESHOLDS.MINIMUM_SAFETY_SCORE) {
                notifications.show({
                    title: '🚨 Safety Violation',
                    message: 'Cannot approve recommendation with safety score below minimum threshold',
                    color: 'red',
                    icon: <IconShieldX size={20} />
                });
                return;
            }

            if (approved && recommendation.regulatory_impact.regulatory_approval_required && userRole !== 'compliance_manager' && userRole !== 'admin') {
                notifications.show({
                    title: '⚖️ Compliance Approval Required',
                    message: 'This recommendation requires compliance manager approval',
                    color: 'orange',
                    icon: <IconGavel size={20} />
                });
                return;
            }

            await onApprove?.(recommendation.id, approved, approvalNotes);
            
            const action = approved ? 'approved' : 'rejected';
            notifications.show({
                title: `✅ Recommendation ${action.charAt(0).toUpperCase() + action.slice(1)}`,
                message: `${recommendation.recommendation_type} optimization has been ${action}`,
                color: approved ? 'green' : 'red',
                icon: approved ? <IconCheck size={20} /> : <IconX size={20} />
            });

            setShowApprovalModal(false);
            setApprovalNotes('');
            setSelectedRecommendation(null);
        } catch (error) {
            notifications.show({
                title: '❌ Approval Failed',
                message: `Failed to ${approved ? 'approve' : 'reject'} recommendation: ${error}`,
                color: 'red'
            });
        }
    }, [onApprove, approvalNotes, userRole]);

    // Monitor implementation progress for life-critical tracking
    const handleMonitorImplementation = useCallback(async (recommendationId: string) => {
        try {
            const progress = await onMonitorImplementation?.(recommendationId);
            if (progress) {
                setImplementationProgress(prev => new Map(prev).set(recommendationId, progress));
            }
        } catch (error) {
            console.error('Failed to monitor implementation:', error);
        }
    }, [onMonitorImplementation]);

    // Get appropriate color for safety score with life-critical thresholds
    const getSafetyScoreColor = useCallback((score: number): string => {
        if (score >= SAFETY_THRESHOLDS.EXCELLENT_SAFETY_SCORE) return 'green';
        if (score >= SAFETY_THRESHOLDS.MINIMUM_SAFETY_SCORE) return 'yellow';
        return 'red';
    }, []);

    // Get regulatory impact color for compliance visualization
    const getRegulatoryImpactColor = useCallback((impact: string): string => {
        switch (impact) {
            case 'NONE': return 'green';
            case 'MINOR': return 'blue';
            case 'MODERATE': return 'yellow';
            case 'SIGNIFICANT': return 'red';
            default: return 'gray';
        }
    }, []);

    // Render comprehensive recommendation card with all life-critical indicators
    const RecommendationCard: React.FC<{ recommendation: OptimizationRecommendation }> = ({ 
        recommendation 
    }) => {
        const simulation = simulationResults.get(recommendation.id);
        const progress = implementationProgress.get(recommendation.id);
        const isBlocked = recommendation.safety_validation.safety_score < SAFETY_THRESHOLDS.MINIMUM_SAFETY_SCORE;
        const requiresApproval = OptimizationUtils.requiresRegulatoryApproval(recommendation);
        const calculatedPriority = OptimizationUtils.calculatePriority(recommendation);

        return (
            <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Card.Section withBorder inheritPadding py="xs">
                    <Group justify="space-between" ta="center">
                        <Group>
                            <ThemeIcon
                                size="lg"
                                color={recommendation.recommendation_type === 'SAFETY' ? 'red' : 
                                       recommendation.recommendation_type === 'COMPLIANCE' ? 'purple' :
                                       recommendation.recommendation_type === 'PERFORMANCE' ? 'blue' :
                                       recommendation.recommendation_type === 'COST' ? 'green' : 'orange'}
                                variant="light"
                            >
                                {recommendation.recommendation_type === 'SAFETY' ? <IconShield size={20} /> :
                                 recommendation.recommendation_type === 'COMPLIANCE' ? <IconScale size={20} /> :
                                 recommendation.recommendation_type === 'PERFORMANCE' ? <IconTrendingUp size={20} /> :
                                 recommendation.recommendation_type === 'COST' ? <IconCurrencyDollar size={20} /> :
                                 <IconAdjustments size={20} />}
                            </ThemeIcon>
                            <div>
                                <Group ta="center" gap="xs">
                                    <Text fw={500} size="lg">
                                        {recommendation.recommendation_type} Optimization
                                    </Text>
                                    {isBlocked && (
                                        <Tooltip label="Blocked due to safety concerns">
                                            <IconShieldX size={16} color="red" />
                                        </Tooltip>
                                    )}
                                    {requiresApproval && (
                                        <Tooltip label="Requires regulatory approval">
                                            <IconGavel size={16} color="orange" />
                                        </Tooltip>
                                    )}
                                </Group>
                                <Text size="xs" c="dimmed">
                                    Priority: {calculatedPriority}/100 • Confidence: {recommendation.confidence_level}%
                                </Text>
                            </div>
                        </Group>
                        <Group>
                            <Badge 
                                color={OptimizationUtils.getSeverityColor(recommendation.severity)}
                                variant="filled"
                            >
                                {recommendation.severity}
                            </Badge>
                            <Badge 
                                color={recommendation.status === RecommendationStatus.APPROVED ? 'green' :
                                       recommendation.status === RecommendationStatus.REJECTED ? 'red' :
                                       recommendation.status === RecommendationStatus.IMPLEMENTED ? 'blue' :
                                       recommendation.status === RecommendationStatus.MONITORING ? 'purple' : 'gray'}
                                variant="light"
                            >
                                {recommendation.status}
                            </Badge>
                        </Group>
                    </Group>
                </Card.Section>

                <Stack mt="md" gap="sm">
                    {/* AI-Generated Description with root cause analysis */}
                    <div>
                        <Text fw={500} size="sm" mb="xs">🤖 AI Analysis</Text>
                        <Text size="sm" mb="xs">{recommendation.description}</Text>
                        {recommendation.root_cause_analysis && (
                            <Accordion variant="contained">
                                <Accordion.Item value="root-cause">
                                    <Accordion.Control>
                                        <Text size="xs" fw={500}>Root Cause Analysis</Text>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <Text size="xs" c="dimmed">{recommendation.root_cause_analysis}</Text>
                                    </Accordion.Panel>
                                </Accordion.Item>
                            </Accordion>
                        )}
                    </div>

                    {/* Comprehensive Safety Validation Status */}
                    <Alert
                        icon={isBlocked ? <IconShieldX size={16} /> : <IconShieldCheck size={16} />}
                        color={getSafetyScoreColor(recommendation.safety_validation.safety_score)}
                        variant="light"
                    >
                        <Group justify="space-between">
                            <div>
                                <Text size="sm" fw={500}>
                                    Safety Score: {recommendation.safety_validation.safety_score}/100
                                </Text>
                                <Group gap="xs" mt="xs">
                                    {recommendation.safety_validation.patient_safety_check && (
                                        <Badge size="xs" color="green" leftSection={<IconEmergencyBed size={12} />}>
                                            Patient Safe
                                        </Badge>
                                    )}
                                    {recommendation.safety_validation.financial_integrity_check && (
                                        <Badge size="xs" color="blue" leftSection={<IconCertificate size={12} />}>
                                            Financial Safe
                                        </Badge>
                                    )}
                                    {recommendation.safety_validation.legal_privilege_check && (
                                        <Badge size="xs" color="purple" leftSection={<IconGavel size={12} />}>
                                            Legal Safe
                                        </Badge>
                                    )}
                                    {!recommendation.safety_validation.emergency_procedure_impact && (
                                        <Badge size="xs" color="green">
                                            Emergency Access OK
                                        </Badge>
                                    )}
                                </Group>
                                {recommendation.safety_validation.safety_blockers.length > 0 && (
                                    <div>
                                        <Text size="xs" c="red" fw={500} mt="xs">Safety Blockers:</Text>
                                        {recommendation.safety_validation.safety_blockers.map((blocker, idx) => (
                                            <Text key={idx} size="xs" c="red">• {blocker}</Text>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {isBlocked && (
                                <Tooltip label="Recommendation blocked due to safety concerns">
                                    <ActionIcon color="red" variant="light" size="lg">
                                        <IconAlertTriangle size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Group>
                    </Alert>

                    {/* Expected Impact with comprehensive metrics */}
                    <div>
                        <Text fw={500} size="sm" mb="xs">📊 Expected Impact</Text>
                        <SimpleGrid cols={3} spacing="xs">
                            <Paper p="xs" withBorder>
                                <Group gap="xs" ta="center">
                                    <IconClock size={16} color="blue" />
                                    <div>
                                        <Text size="xs" fw={500}>Time Reduction</Text>
                                        <Text size="sm" c="green" fw={600}>
                                            -{recommendation.estimated_impact.performance_improvement.execution_time_reduction_pct}%
                                        </Text>
                                    </div>
                                </Group>
                            </Paper>
                            <Paper p="xs" withBorder>
                                <Group gap="xs" ta="center">
                                    <IconCurrencyDollar size={16} color="green" />
                                    <div>
                                        <Text size="xs" fw={500}>Annual Savings</Text>
                                        <Text size="sm" c="green" fw={600}>
                                            ${recommendation.estimated_impact.cost_impact.annual_savings_usd.toLocaleString()}
                                        </Text>
                                    </div>
                                </Group>
                            </Paper>
                            <Paper p="xs" withBorder>
                                <Group gap="xs" ta="center">
                                    <IconTrendingUp size={16} color="blue" />
                                    <div>
                                        <Text size="xs" fw={500}>Reliability</Text>
                                        <Text size="sm" c="green" fw={600}>
                                            +{recommendation.estimated_impact.reliability_improvement.availability_increase_pct}%
                                        </Text>
                                    </div>
                                </Group>
                            </Paper>
                        </SimpleGrid>
                        
                        {/* ROI Information */}
                        <Paper p="xs" withBorder mt="xs">
                            <Group justify="center">
                                <Text size="sm">
                                    <strong>ROI:</strong> {OptimizationUtils.formatROI(recommendation.estimated_impact.cost_impact)}
                                </Text>
                                <Text size="sm">
                                    <strong>Implementation:</strong> {recommendation.implementation_effort}
                                </Text>
                            </Group>
                        </Paper>
                    </div>

                    {/* Comprehensive Regulatory Impact Assessment */}
                    <div>
                        <Text fw={500} size="sm" mb="xs">🏛️ Regulatory Compliance Impact</Text>
                        <Group gap="xs" mb="xs">
                            <Badge size="xs" color={getRegulatoryImpactColor(recommendation.regulatory_impact.hipaa_impact)}>
                                HIPAA: {recommendation.regulatory_impact.hipaa_impact}
                            </Badge>
                            <Badge size="xs" color={getRegulatoryImpactColor(recommendation.regulatory_impact.sox_impact)}>
                                SOX: {recommendation.regulatory_impact.sox_impact}
                            </Badge>
                            <Badge size="xs" color={getRegulatoryImpactColor(recommendation.regulatory_impact.fda_21_cfr_part_11_impact)}>
                                FDA: {recommendation.regulatory_impact.fda_21_cfr_part_11_impact}
                            </Badge>
                            <Badge size="xs" color={getRegulatoryImpactColor(recommendation.regulatory_impact.gdpr_impact)}>
                                GDPR: {recommendation.regulatory_impact.gdpr_impact}
                            </Badge>
                            <Badge size="xs" color={getRegulatoryImpactColor(recommendation.regulatory_impact.pci_dss_impact)}>
                                PCI: {recommendation.regulatory_impact.pci_dss_impact}
                            </Badge>
                        </Group>
                        <Group justify="space-between" ta="center">
                            <Text size="xs">
                                Compliance Risk: {recommendation.regulatory_impact.compliance_risk_score}/100
                            </Text>
                            {requiresApproval && (
                                <Badge size="xs" color="red" variant="filled">
                                    Regulatory Approval Required
                                </Badge>
                            )}
                        </Group>
                        <Progress 
                            value={recommendation.regulatory_impact.compliance_risk_score} 
                            color={recommendation.regulatory_impact.compliance_risk_score >= complianceThresholds.critical ? 'red' : 
                                   recommendation.regulatory_impact.compliance_risk_score >= complianceThresholds.warning ? 'yellow' : 'green'} 
                            size="xs" 
                        />
                    </div>

                    {/* Implementation Progress (if applicable) */}
                    {progress && recommendation.status === RecommendationStatus.IMPLEMENTED && (
                        <div>
                            <Text fw={500} size="sm" mb="xs">🚀 Implementation Progress</Text>
                            <Group justify="space-between" mb="xs">
                                <Text size="sm">Current Phase: {progress.current_phase}</Text>
                                <Text size="sm">{Math.round(progress.overall_progress * 100)}%</Text>
                            </Group>
                            <Progress value={progress.overall_progress * 100} />
                            {progress.issues.filter(i => i.severity === 'ERROR' || i.severity === 'CRITICAL').length > 0 && (
                                <Alert icon={<IconAlertTriangle size={16} />} color="orange" mt="xs">
                                    {progress.issues.filter(i => i.severity === 'ERROR' || i.severity === 'CRITICAL').length} active issues
                                </Alert>
                            )}
                        </div>
                    )}

                    {/* Impact Simulation Results */}
                    {simulation && (
                        <div>
                            <Text fw={500} size="sm" mb="xs">🎯 Impact Simulation</Text>
                            <SimpleGrid cols={2} spacing="xs">
                                <Paper p="xs" withBorder>
                                    <Text size="xs" c="dimmed">Projected ROI</Text>
                                    <Text size="lg" fw={700} c="green">
                                        {simulation.roi_percentage}%
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                        ({simulation.roi_months} months)
                                    </Text>
                                </Paper>
                                <Paper p="xs" withBorder>
                                    <Text size="xs" c="dimmed">Confidence</Text>
                                    <Text size="lg" fw={700}>
                                        {Math.round(simulation.confidence_interval.confidence * 100)}%
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                        ±{Math.round((simulation.confidence_interval.upper - simulation.confidence_interval.lower) / 2)}%
                                    </Text>
                                </Paper>
                            </SimpleGrid>
                            {simulation.risk_factors.length > 0 && (
                                <Alert icon={<IconInfoCircle size={16} />} color="blue" mt="xs">
                                    {simulation.risk_factors.length} risk factors identified
                                </Alert>
                            )}
                        </div>
                    )}

                    {/* Evidence and Proposed Changes Accordion */}
                    {(recommendation.evidence.length > 0 || recommendation.proposed_changes.length > 0) && (
                        <Accordion variant="contained">
                            {recommendation.evidence.length > 0 && (
                                <Accordion.Item value="evidence">
                                    <Accordion.Control>
                                        <Text size="sm" fw={500}>📋 Supporting Evidence ({recommendation.evidence.length})</Text>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <Stack gap="xs">
                                            {recommendation.evidence.slice(0, 3).map((evidence, idx) => (
                                                <Paper key={idx} p="xs" withBorder>
                                                    <Group justify="space-between" ta="left">
                                                        <div>
                                                            <Text size="xs" fw={500}>{evidence.evidence_type}</Text>
                                                            <Text size="xs" c="dimmed">{evidence.source}</Text>
                                                        </div>
                                                        <Badge size="xs" variant="light">
                                                            Weight: {Math.round(evidence.weight * 100)}%
                                                        </Badge>
                                                    </Group>
                                                </Paper>
                                            ))}
                                            {recommendation.evidence.length > 3 && (
                                                <Text size="xs" c="dimmed" ta="center">
                                                    ... and {recommendation.evidence.length - 3} more
                                                </Text>
                                            )}
                                        </Stack>
                                    </Accordion.Panel>
                                </Accordion.Item>
                            )}
                            
                            {recommendation.proposed_changes.length > 0 && (
                                <Accordion.Item value="changes">
                                    <Accordion.Control>
                                        <Text size="sm" fw={500}>⚙️ Proposed Changes ({recommendation.proposed_changes.length})</Text>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <Stack gap="xs">
                                            {recommendation.proposed_changes.slice(0, 2).map((change, idx) => (
                                                <Paper key={idx} p="xs" withBorder>
                                                    <Group justify="space-between" ta="left">
                                                        <div>
                                                            <Text size="xs" fw={500}>{change.change_type}</Text>
                                                            <Text size="xs" c="dimmed">{change.target_component}</Text>
                                                            <Text size="xs" mt="xs">{change.change_rationale}</Text>
                                                        </div>
                                                        <Badge size="xs" color={OptimizationUtils.getSeverityColor(change.risk_level)}>
                                                            {change.risk_level} Risk
                                                        </Badge>
                                                    </Group>
                                                </Paper>
                                            ))}
                                            {recommendation.proposed_changes.length > 2 && (
                                                <Text size="xs" c="dimmed" ta="center">
                                                    ... and {recommendation.proposed_changes.length - 2} more changes
                                                </Text>
                                            )}
                                        </Stack>
                                    </Accordion.Panel>
                                </Accordion.Item>
                            )}
                        </Accordion>
                    )}

                    {/* Action Buttons with comprehensive permissions */}
                    <Group justify="space-between" mt="md">
                        <Group>
                            <Button
                                leftSection={<IconEye size={16} />}
                                variant="light"
                                size="sm"
                                onClick={() => setSelectedRecommendation(recommendation)}
                            >
                                Details
                            </Button>
                            
                            {!simulation && (
                                <Button
                                    leftSection={<IconAnalyze size={16} />}
                                    variant="light"
                                    size="sm"
                                    onClick={() => handleSimulateImpact(recommendation)}
                                    loading={simulationResults.has(recommendation.id)}
                                >
                                    Simulate
                                </Button>
                            )}

                            {(userRole === 'admin' || userRole === 'implementer') && recommendation.status === RecommendationStatus.IMPLEMENTED && (
                                <Button
                                    leftSection={<IconChartBar size={16} />}
                                    variant="light"
                                    size="sm"
                                    onClick={() => handleMonitorImplementation(recommendation.id)}
                                >
                                    Monitor
                                </Button>
                            )}
                        </Group>

                        {/* Approval/Implementation Actions */}
                        {(userRole === 'approver' || userRole === 'safety_officer' || userRole === 'compliance_manager' || userRole === 'admin') && 
                         recommendation.status === RecommendationStatus.PENDING && !isBlocked && (
                            <Group gap="xs">
                                <Button
                                    leftSection={<IconCheck size={16} />}
                                    color="green"
                                    size="sm"
                                    onClick={() => {
                                        setSelectedRecommendation(recommendation);
                                        setShowApprovalModal(true);
                                    }}
                                >
                                    Approve
                                </Button>
                                <Button
                                    leftSection={<IconX size={16} />}
                                    color="red"
                                    variant="light"
                                    size="sm"
                                    onClick={() => {
                                        modals.openConfirmModal({
                                            title: 'Reject Recommendation',
                                            children: (
                                                <div>
                                                    <Text size="sm" mb="md">
                                                        Are you sure you want to reject this optimization recommendation?
                                                    </Text>
                                                    <Textarea
                                                        label="Rejection Reason"
                                                        placeholder="Provide reason for rejection..."
                                                        required
                                                        id="rejection-reason"
                                                    />
                                                </div>
                                            ),
                                            labels: { confirm: 'Reject', cancel: 'Cancel' },
                                            confirmProps: { color: 'red' },
                                            onConfirm: () => {
                                                const reason = (document.getElementById('rejection-reason') as HTMLTextAreaElement)?.value || 'No reason provided';
                                                onReject?.(recommendation.id, reason);
                                            },
                                        });
                                    }}
                                >
                                    Reject
                                </Button>
                            </Group>
                        )}

                        {(userRole === 'implementer' || userRole === 'admin') && recommendation.status === RecommendationStatus.APPROVED && (
                            <Button
                                leftSection={<IconPlayerPlay size={16} />}
                                color="blue"
                                size="sm"
                                onClick={() => {
                                    setSelectedRecommendation(recommendation);
                                    setShowImplementationModal(true);
                                }}
                            >
                                Implement
                            </Button>
                        )}

                        {isBlocked && (
                            <Tooltip label="Recommendation blocked due to safety concerns">
                                <Button
                                    leftSection={<IconShieldX size={16} />}
                                    color="red"
                                    variant="light"
                                    size="sm"
                                    disabled
                                >
                                    Blocked
                                </Button>
                            </Tooltip>
                        )}
                    </Group>
                </Stack>
            </Card>
        );
    };

    // Main render with comprehensive tabs and controls
    return (
        <Container size="xl" p="lg">
            {/* Comprehensive Header with Life-Critical Indicators */}
            <Group justify="space-between" mb="xl">
                <div>
                    <Group ta="left" gap="md">
                        <div>
                            <Title order={1}>
                                🤖 AI Optimization Recommendations
                            </Title>
                            <Text c="dimmed" size="sm">
                                Intelligent workflow optimization with life-critical safety validation
                            </Text>
                            <Text c="dimmed" size="xs" mt="xs">
                                Workflow: {workflowName} • Tenant: {tenant}
                            </Text>
                        </div>
                        
                        {/* Safety Status Indicator */}
                        {lifeCriticalMode && (
                            <Paper p="md" withBorder>
                                <Group ta="center" gap="xs">
                                    <ThemeIcon color={safetyMetrics.averageSafetyScore >= SAFETY_THRESHOLDS.EXCELLENT_SAFETY_SCORE ? 'green' : 
                                                     safetyMetrics.averageSafetyScore >= SAFETY_THRESHOLDS.MINIMUM_SAFETY_SCORE ? 'yellow' : 'red'}>
                                        <IconShield size={16} />
                                    </ThemeIcon>
                                    <div>
                                        <Text size="sm" fw={500}>
                                            Safety Score
                                        </Text>
                                        <Text size="lg" fw={700} c={getSafetyScoreColor(safetyMetrics.averageSafetyScore)}>
                                            {Math.round(safetyMetrics.averageSafetyScore)}/100
                                        </Text>
                                    </div>
                                </Group>
                            </Paper>
                        )}
                    </Group>
                </div>
                
                <Group>
                    {/* AI Generation Button */}
                    {(userRole === 'admin' || userRole === 'implementer' || permissions.includes('generate')) && (
                        <Button
                            leftSection={<IconBrain size={16} />}
                            loading={isGenerating}
                            onClick={() => setShowGenerationModal(true)}
                            gradient={{ from: 'blue', to: 'purple' }}
                            variant="gradient"
                        >
                            Generate AI Recommendations
                        </Button>
                    )}
                    
                    {/* Export Options */}
                    <Button
                        leftSection={<IconDownload size={16} />}
                        variant="light"
                        onClick={() => onExportRecommendations?.('json')}
                    >
                        Export
                    </Button>
                    
                    <Button
                        leftSection={<IconSettings size={16} />}
                        variant="light"
                    >
                        Settings
                    </Button>
                </Group>
            </Group>

            {/* Comprehensive Metrics Overview */}
            <SimpleGrid cols={5} spacing="md" mb="xl">
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                                Total Recommendations
                            </Text>
                            <Text fw={700} size="xl">
                                {safetyMetrics.total}
                            </Text>
                        </div>
                        <ThemeIcon color="blue" size={38} radius="md">
                            <IconRobot size={20} />
                        </ThemeIcon>
                    </Group>
                </Paper>

                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                                Safe to Implement
                            </Text>
                            <Text fw={700} size="xl" c="green">
                                {safetyMetrics.safe}
                            </Text>
                            <Text size="xs" c="dimmed">
                                {Math.round(safetyMetrics.safetyRate)}%
                            </Text>
                        </div>
                        <ThemeIcon color="green" size={38} radius="md">
                            <IconShieldCheck size={20} />
                        </ThemeIcon>
                    </Group>
                </Paper>

                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                                Blocked (Safety)
                            </Text>
                            <Text fw={700} size="xl" c="red">
                                {safetyMetrics.blocked}
                            </Text>
                            <Text size="xs" c="dimmed">
                                {Math.round(safetyMetrics.blockageRate)}%
                            </Text>
                        </div>
                        <ThemeIcon color="red" size={38} radius="md">
                            <IconShieldX size={20} />
                        </ThemeIcon>
                    </Group>
                </Paper>

                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                                Implemented
                            </Text>
                            <Text fw={700} size="xl" c="blue">
                                {safetyMetrics.implemented}
                            </Text>
                            <Text size="xs" c="dimmed">
                                {Math.round(safetyMetrics.implementationRate)}%
                            </Text>
                        </div>
                        <ThemeIcon color="blue" size={38} radius="md">
                            <IconPlayerPlay size={20} />
                        </ThemeIcon>
                    </Group>
                </Paper>

                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                                Total Savings
                            </Text>
                            <Text fw={700} size="xl" c="green">
                                ${Math.round(safetyMetrics.totalSavings / 1000)}K
                            </Text>
                            <Text size="xs" c="dimmed">
                                Annual
                            </Text>
                        </div>
                        <ThemeIcon color="green" size={38} radius="md">
                            <IconCurrencyDollar size={20} />
                        </ThemeIcon>
                    </Group>
                </Paper>
            </SimpleGrid>

            {/* Comprehensive Tab Interface */}
            <Tabs value={activeTab} onChange={(value: string | null) => setActiveTab(value || 'recommendations')}>
                <Tabs.List>
                    <Tabs.Tab value="recommendations" leftSection={<IconRobot size={16} />}>
                        Recommendations ({filteredAndSortedRecommendations.length})
                    </Tabs.Tab>
                    <Tabs.Tab value="analytics" leftSection={<IconReportAnalytics size={16} />}>
                        Impact Analytics
                    </Tabs.Tab>
                    <Tabs.Tab value="safety" leftSection={<IconShieldCheck size={16} />}>
                        Safety Dashboard
                    </Tabs.Tab>
                    <Tabs.Tab value="compliance" leftSection={<IconScale size={16} />}>
                        Regulatory Compliance
                    </Tabs.Tab>
                    <Tabs.Tab value="implementation" leftSection={<IconPlayerPlay size={16} />}>
                        Implementation Tracking
                    </Tabs.Tab>
                </Tabs.List>

                {/* Main Recommendations Tab with Advanced Filtering */}
                <Tabs.Panel value="recommendations" pt="md">
                    {/* Advanced Filter and Sort Controls */}
                    <Paper p="md" withBorder mb="md">
                        <Group justify="space-between">
                            <Group>
                                <Select
                                    placeholder="Filter by severity"
                                    value={filterSeverity}
                                    onChange={(value: string | null) => setFilterSeverity(value)}
                                    data={[
                                        { value: 'CRITICAL', label: '🔴 Critical' },
                                        { value: 'HIGH', label: '🟠 High' },
                                        { value: 'MEDIUM', label: '🟡 Medium' },
                                        { value: 'LOW', label: '🔵 Low' }
                                    ]}
                                    clearable
                                    w={150}
                                />
                                <Select
                                    placeholder="Filter by type"
                                    value={filterType}
                                    onChange={(value: string | null) => setFilterType(value)}
                                    data={[
                                        { value: 'PERFORMANCE', label: '🚀 Performance' },
                                        { value: 'COST', label: '💰 Cost' },
                                        { value: 'RELIABILITY', label: '🛡️ Reliability' },
                                        { value: 'COMPLIANCE', label: '⚖️ Compliance' },
                                        { value: 'SAFETY', label: '🚨 Safety' }
                                    ]}
                                    clearable
                                    w={150}
                                />
                                <Select
                                    placeholder="Filter by status"
                                    value={filterStatus}
                                    onChange={(value: string | null) => setFilterStatus(value)}
                                    data={[
                                        { value: 'PENDING', label: '⏳ Pending' },
                                        { value: 'APPROVED', label: '✅ Approved' },
                                        { value: 'REJECTED', label: '❌ Rejected' },
                                        { value: 'IMPLEMENTED', label: '🚀 Implemented' },
                                        { value: 'MONITORING', label: '📊 Monitoring' }
                                    ]}
                                    clearable
                                    w={150}
                                />
                            </Group>
                            
                            <Group>
                                <Select
                                    label="Sort by"
                                    value={sortBy}
                                    onChange={(value: string | null) => setSortBy((value as any) || sortBy)}
                                    data={[
                                        { value: 'priority', label: 'Priority Score' },
                                        { value: 'safety', label: 'Safety Score' },
                                        { value: 'savings', label: 'Cost Savings' },
                                        { value: 'created', label: 'Created Date' }
                                    ]}
                                    w={120}
                                />
                                <Select
                                    value={sortOrder}
                                    onChange={(value: string | null) => setSortOrder((value as any) || sortOrder)}
                                    data={[
                                        { value: 'desc', label: 'Desc' },
                                        { value: 'asc', label: 'Asc' }
                                    ]}
                                    w={80}
                                />
                            </Group>
                        </Group>
                    </Paper>

                    {/* Recommendations Display */}
                    {filteredAndSortedRecommendations.length === 0 ? (
                        <Center>
                            <Paper p="xl" withBorder>
                                <Stack ta="center" gap="md">
                                    <IconRobot size={48} color="gray" />
                                    <div>
                                        <Text size="lg" fw={500} ta="center">
                                            {recommendations.length === 0 ? 'No Recommendations Available' : 'No Matching Recommendations'}
                                        </Text>
                                        <Text c="dimmed" ta="center" mt="xs">
                                            {recommendations.length === 0 ? 
                                                'Generate AI-powered optimization recommendations to improve workflow performance' :
                                                'Adjust your filters to see more recommendations'
                                            }
                                        </Text>
                                    </div>
                                    {recommendations.length === 0 && (
                                        <Button
                                            leftSection={<IconBrain size={16} />}
                                            onClick={() => setShowGenerationModal(true)}
                                            loading={isGenerating}
                                        >
                                            Generate Recommendations
                                        </Button>
                                    )}
                                </Stack>
                            </Paper>
                        </Center>
                    ) : (
                        <Grid>
                            {filteredAndSortedRecommendations.map((recommendation) => (
                                <Grid.Col key={recommendation.id} span={12}>
                                    <RecommendationCard recommendation={recommendation} />
                                </Grid.Col>
                            ))}
                        </Grid>
                    )}
                </Tabs.Panel>

                {/* Impact Analytics Tab */}
                <Tabs.Panel value="analytics" pt="md">
                    <SimpleGrid cols={2} spacing="md">
                        <Paper p="lg" withBorder>
                            <Title order={3} mb="md">Performance Impact Analysis</Title>
                            {Array.from(simulationResults.values()).length > 0 ? (
                                <Stack gap="md">
                                    {Array.from(simulationResults.entries()).map(([id, simulation]) => (
                                        <Paper key={id} p="md" withBorder>
                                            <Group justify="space-between" mb="sm">
                                                <Text fw={500}>Recommendation {id.slice(-8)}</Text>
                                                <Badge color="green">{simulation.roi_percentage}% ROI</Badge>
                                            </Group>
                                            <SimpleGrid cols={3} spacing="xs">
                                                <div>
                                                    <Text size="xs" c="dimmed">Time Improvement</Text>
                                                    <Text fw={600} c="green">-{simulation.execution_time_improvement}%</Text>
                                                </div>
                                                <div>
                                                    <Text size="xs" c="dimmed">Error Reduction</Text>
                                                    <Text fw={600} c="green">-{simulation.error_rate_improvement}%</Text>
                                                </div>
                                                <div>
                                                    <Text size="xs" c="dimmed">Throughput</Text>
                                                    <Text fw={600} c="blue">+{simulation.throughput_improvement}%</Text>
                                                </div>
                                            </SimpleGrid>
                                            <Progress
                                                value={simulation.confidence_interval.confidence * 100}
                                                mt="sm"
                                            />
                                        </Paper>
                                    ))}
                                </Stack>
                            ) : (
                                <Text c="dimmed" ta="center">
                                    Run impact simulations on recommendations to view detailed analytics
                                </Text>
                            )}
                        </Paper>
                        
                        <Paper p="lg" withBorder>
                            <Title order={3} mb="md">Financial Impact Summary</Title>
                            <Stack gap="sm">
                                <Group justify="space-between">
                                    <Text>Total Projected Savings</Text>
                                    <Text fw={600} c="green" size="lg">
                                        ${recommendations.reduce((sum, r) => sum + r.estimated_impact.cost_impact.annual_savings_usd, 0).toLocaleString()}
                                    </Text>
                                </Group>
                                <Group justify="space-between">
                                    <Text>Implementation Costs</Text>
                                    <Text fw={600} c="red">
                                        ${recommendations.reduce((sum, r) => sum + r.estimated_impact.cost_impact.implementation_cost_usd, 0).toLocaleString()}
                                    </Text>
                                </Group>
                                <Group justify="space-between">
                                    <Text>Net Annual Benefit</Text>
                                    <Text fw={700} c="green" size="lg">
                                        ${recommendations.reduce((sum, r) => sum + r.estimated_impact.cost_impact.annual_savings_usd - r.estimated_impact.cost_impact.implementation_cost_usd, 0).toLocaleString()}
                                    </Text>
                                </Group>
                                <Divider />
                                <Group justify="space-between">
                                    <Text>Average ROI Period</Text>
                                    <Text fw={600}>
                                        {recommendations.length > 0 ? 
                                            Math.round(recommendations.reduce((sum, r) => sum + r.estimated_impact.cost_impact.roi_months, 0) / recommendations.length) 
                                            : 0} months
                                    </Text>
                                </Group>
                            </Stack>
                        </Paper>
                    </SimpleGrid>
                </Tabs.Panel>

                {/* Safety Dashboard Tab */}
                <Tabs.Panel value="safety" pt="md">
                    <SimpleGrid cols={2} spacing="md">
                        <Paper p="lg" withBorder>
                            <Title order={4} mb="md">Safety Score Distribution</Title>
                            <Stack gap="sm">
                                {[
                                    { label: `Excellent (≥${safetyThresholds.EXCELLENT})`, count: recommendations.filter(r => r.safety_validation.safety_score >= safetyThresholds.EXCELLENT).length, color: 'green' },
                                    { label: `Warning (≥${safetyThresholds.WARNING} and < ${safetyThresholds.EXCELLENT})`, count: recommendations.filter(r => r.safety_validation.safety_score >= safetyThresholds.WARNING && r.safety_validation.safety_score < safetyThresholds.EXCELLENT).length, color: 'yellow' },
                                    { label: `Critical (≥${safetyThresholds.CRITICAL} and < ${safetyThresholds.WARNING})`, count: recommendations.filter(r => r.safety_validation.safety_score >= safetyThresholds.CRITICAL && r.safety_validation.safety_score < safetyThresholds.WARNING).length, color: 'orange' },
                                    { label: `Unsafe (<${safetyThresholds.CRITICAL})`, count: recommendations.filter(r => r.safety_validation.safety_score < safetyThresholds.CRITICAL).length, color: 'red' }
                                ].map((item, idx) => (
                                    <div key={idx}>
                                        <Group justify="space-between" mb="xs">
                                            <Text size="sm">{item.label}</Text>
                                            <Text fw={600}>{item.count}</Text>
                                        </Group>
                                        <Progress value={recommendations.length > 0 ? (item.count / recommendations.length) * 100 : 0} color={item.color} />
                                    </div>
                                ))}
                            </Stack>
                        </Paper>
                        
                        <Paper p="lg" withBorder>
                            <Title order={4} mb="md">Life-Critical Safety Checks</Title>
                            <Stack gap="sm">
                                <Group justify="space-between">
                                    <Group>
                                        <IconEmergencyBed size={16} />
                                        <Text size="sm">Patient Safety Validated</Text>
                                    </Group>
                                    <Group>
                                        <Text fw={600}>
                                            {recommendations.filter(r => r.safety_validation.patient_safety_check).length}/{recommendations.length}
                                        </Text>
                                        <IconCheck size={16} color="green" />
                                    </Group>
                                </Group>
                                <Group justify="space-between">
                                    <Group>
                                        <IconCertificate size={16} />
                                        <Text size="sm">Financial Integrity Protected</Text>
                                    </Group>
                                    <Group>
                                        <Text fw={600}>
                                            {recommendations.filter(r => r.safety_validation.financial_integrity_check).length}/{recommendations.length}
                                        </Text>
                                        <IconCheck size={16} color="green" />
                                    </Group>
                                </Group>
                                <Group justify="space-between">
                                    <Group>
                                        <IconGavel size={16} />
                                        <Text size="sm">Legal Privilege Maintained</Text>
                                    </Group>
                                    <Group>
                                        <Text fw={600}>
                                            {recommendations.filter(r => r.safety_validation.legal_privilege_check).length}/{recommendations.length}
                                        </Text>
                                        <IconCheck size={16} color="green" />
                                    </Group>
                                </Group>
                                <Group justify="space-between">
                                    <Group>
                                        <IconShield size={16} />
                                        <Text size="sm">Emergency Access Preserved</Text>
                                    </Group>
                                    <Group>
                                        <Text fw={600}>
                                            {recommendations.filter(r => !r.safety_validation.emergency_procedure_impact).length}/{recommendations.length}
                                        </Text>
                                        <IconCheck size={16} color="green" />
                                    </Group>
                                </Group>
                            </Stack>

                            <Divider my="md" />

                            <Alert icon={<IconShieldCheck size={16} />} color="green" mb="md">
                                <Text fw={500}>Safety Status: OPERATIONAL</Text>
                                <Text size="sm">
                                    All safety checks are passing. {safetyMetrics.blocked} recommendations blocked for safety.
                                </Text>
                            </Alert>

                            {safetyMetrics.blocked > 0 && (
                                <Alert icon={<IconAlertTriangle size={16} />} color="orange">
                                    <Text fw={500}>Safety Blockers Active</Text>
                                    <Text size="sm">
                                        {safetyMetrics.blocked} recommendations are blocked due to safety concerns and require review.
                                    </Text>
                                </Alert>
                            )}
                        </Paper>
                    </SimpleGrid>
                </Tabs.Panel>

                {/* Regulatory Compliance Tab */}
                <Tabs.Panel value="compliance" pt="md">
                    <Paper p="lg" withBorder>
                        <Title order={3} mb="md">Regulatory Compliance Dashboard</Title>
                        <SimpleGrid cols={5} spacing="md">
                            {['HIPAA', 'SOX', 'FDA 21 CFR Part 11', 'GDPR', 'PCI DSS'].map((regulation) => {
                                const impactField = regulation === 'HIPAA' ? 'hipaa_impact' :
                                                   regulation === 'SOX' ? 'sox_impact' :
                                                   regulation === 'FDA 21 CFR Part 11' ? 'fda_21_cfr_part_11_impact' :
                                                   regulation === 'GDPR' ? 'gdpr_impact' : 'pci_dss_impact';
                                
                                const significantImpact = recommendations.filter(r => 
                                    (r.regulatory_impact as any)[impactField] === 'SIGNIFICANT'
                                ).length;
                                
                                const complianceScore = recommendations.length > 0 ? 
                                    Math.max(0, 100 - (significantImpact / recommendations.length) * 100) : 100;

                                return (
                                    <Paper key={regulation} p="md" withBorder>
                                        <Stack ta="center" gap="sm">
                                            <Text size="sm" fw={500} ta="center">{regulation}</Text>
                                            <RingProgress
                                                size={80}
                                                thickness={8}
                                                sections={[{ value: complianceScore, color: complianceScore >= 90 ? 'green' : complianceScore >= 70 ? 'yellow' : 'red' }]}
                                                label={
                                                    <Text c={complianceScore >= 90 ? 'green' : complianceScore >= 70 ? 'yellow' : 'red'} fw={700} ta="center" size="sm">
                                                        {Math.round(complianceScore)}%
                                                    </Text>
                                                }
                                            />
                                            <Badge 
                                                size="xs" 
                                                color={complianceScore >= 90 ? 'green' : complianceScore >= 70 ? 'yellow' : 'red'}
                                            >
                                                {complianceScore >= 90 ? 'Compliant' : complianceScore >= 70 ? 'Minor Issues' : 'Major Issues'}
                                            </Badge>
                                            {significantImpact > 0 && (
                                                <Text size="xs" c="red">
                                                    {significantImpact} significant impact(s)
                                                </Text>
                                            )}
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </SimpleGrid>

                        <Divider my="xl" />

                        <Title order={4} mb="md">Regulatory Risk Assessment</Title>
                        <Table>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Recommendation</Table.Th>
                                    <Table.Th>Risk Score</Table.Th>
                                    <Table.Th>Primary Impact</Table.Th>
                                    <Table.Th>Approval Required</Table.Th>
                                    <Table.Th>Status</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {recommendations
                                    .filter(r => r.regulatory_impact.compliance_risk_score >= complianceThresholds.warning)
                                    .slice(0, 10)
                                    .map((rec) => {
                                        const primaryImpact = Object.entries(rec.regulatory_impact)
                                            .filter(([key]) => key.endsWith('_impact'))
                                            .find(([, value]) => value === 'SIGNIFICANT' || value === 'MODERATE')?.[0]?.replace('_impact', '').toUpperCase() || 'NONE';

                                        return (
                                            <Table.Tr key={rec.id}>
                                                <Table.Td>
                                                    <Text size="sm" fw={500}>{rec.recommendation_type}</Text>
                                                    <Text size="xs" c="dimmed" truncate style={{ maxWidth: 200 }}>
                                                        {rec.description}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Badge color={rec.regulatory_impact.compliance_risk_score >= complianceThresholds.critical ? 'red' : 'yellow'}>
                                                        {rec.regulatory_impact.compliance_risk_score}/100
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">{primaryImpact}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    {rec.regulatory_impact.regulatory_approval_required ? (
                                                        <IconCheck size={16} color="orange" />
                                                    ) : (
                                                        <IconX size={16} color="green" />
                                                    )}
                                                </Table.Td>
                                                <Table.Td>
                                                    <Badge color={rec.status === RecommendationStatus.APPROVED ? 'green' : rec.status === RecommendationStatus.REJECTED ? 'red' : 'yellow'}>
                                                        {rec.status}
                                                    </Badge>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                            </Table.Tbody>
                        </Table>
                    </Paper>
                </Tabs.Panel>

                {/* Implementation Tracking Tab */}
                <Tabs.Panel value="implementation" pt="md">
                    <Paper p="lg" withBorder>
                        <Title order={3} mb="md">Implementation Progress Tracking</Title>
                        {Array.from(implementationProgress.entries()).length > 0 ? (
                            <Stack gap="md">
                                {Array.from(implementationProgress.entries()).map(([id, progress]) => (
                                    <Paper key={id} p="md" withBorder>
                                        <Group justify="space-between" mb="sm">
                                            <div>
                                                <Text fw={500}>Recommendation {id.slice(-8)}</Text>
                                                <Text size="sm" c="dimmed">Phase: {progress.current_phase}</Text>
                                            </div>
                                            <Text fw={600}>{Math.round(progress.overall_progress * 100)}% Complete</Text>
                                        </Group>
                                        
                                        <Progress value={progress.overall_progress * 100} mb="sm" />
                                        
                                        <Group justify="space-between">
                                            <Text size="sm">ETA: {new Date(progress.estimated_completion).toLocaleDateString()}</Text>
                                            <Group>
                                                {progress.issues.filter(i => i.severity === 'CRITICAL').length > 0 && (
                                                    <Badge color="red" size="sm">
                                                        {progress.issues.filter(i => i.severity === 'CRITICAL').length} Critical Issues
                                                    </Badge>
                                                )}
                                                {progress.issues.filter(i => i.severity === 'WARNING').length > 0 && (
                                                    <Badge color="yellow" size="sm">
                                                        {progress.issues.filter(i => i.severity === 'WARNING').length} Warnings
                                                    </Badge>
                                                )}
                                            </Group>
                                        </Group>
                                        
                                        {progress.metrics_snapshot && (
                                            <SimpleGrid cols={4} spacing="xs" mt="sm">
                                                <div>
                                                    <Text size="xs" c="dimmed">Exec Time</Text>
                                                    <Text size="sm" fw={600}>{progress.metrics_snapshot.execution_time}ms</Text>
                                                </div>
                                                <div>
                                                    <Text size="xs" c="dimmed">Error Rate</Text>
                                                    <Text size="sm" fw={600}>{(progress.metrics_snapshot.error_rate * 100).toFixed(2)}%</Text>
                                                </div>
                                                <div>
                                                    <Text size="xs" c="dimmed">Throughput</Text>
                                                    <Text size="sm" fw={600}>{progress.metrics_snapshot.throughput}/hr</Text>
                                                </div>
                                                <div>
                                                    <Text size="xs" c="dimmed">Resource Usage</Text>
                                                    <Text size="sm" fw={600}>{Math.round(progress.metrics_snapshot.resource_utilization * 100)}%</Text>
                                                </div>
                                            </SimpleGrid>
                                        )}
                                    </Paper>
                                ))}
                            </Stack>
                        ) : (
                            <Center>
                                <div>
                                    <Text ta="center" c="dimmed" mb="md">No active implementations</Text>
                                    <Text size="sm" ta="center" c="dimmed">
                                        Approved recommendations will appear here once implementation begins
                                    </Text>
                                </div>
                            </Center>
                        )}
                    </Paper>
                </Tabs.Panel>
            </Tabs>

            {/* Comprehensive Modal Dialogs */}
            
            {/* AI Generation Modal */}
            <Modal
                opened={showGenerationModal}
                onClose={() => setShowGenerationModal(false)}
                title="Generate AI Optimization Recommendations"
                size="lg"
            >
                <Stack gap="md">
                    <Alert icon={<IconBrain size={16} />} color="blue">
                        <Text fw={500}>AI-Powered Analysis</Text>
                        <Text size="sm">
                            Generate intelligent optimization recommendations using advanced AI analysis of your workflow performance data.
                        </Text>
                    </Alert>

                    <SimpleGrid cols={2} spacing="md">
                        <div>
                            <Text fw={500} mb="sm">Analysis Areas</Text>
                            <Stack gap="xs">
                                <Checkbox
                                    label="Performance Optimization"
                                    checked={generationOptions.include_performance}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGenerationOptions(prev => ({ ...prev, include_performance: e.currentTarget.checked }))}
                                />
                                <Checkbox
                                    label="Cost Optimization"
                                    checked={generationOptions.include_cost}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGenerationOptions(prev => ({ ...prev, include_cost: e.currentTarget.checked }))}
                                />
                                <Checkbox
                                    label="Reliability Improvements"
                                    checked={generationOptions.include_reliability}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGenerationOptions(prev => ({ ...prev, include_reliability: e.currentTarget.checked }))}
                                />
                                <Checkbox
                                    label="Compliance Optimization"
                                    checked={generationOptions.include_compliance}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGenerationOptions(prev => ({ ...prev, include_compliance: e.currentTarget.checked }))}
                                />
                                <Checkbox
                                    label="Safety Enhancements"
                                    checked={generationOptions.include_safety}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGenerationOptions(prev => ({ ...prev, include_safety: e.currentTarget.checked }))}
                                />
                            </Stack>
                        </div>

                        <div>
                            <Stack gap="md">
                                <Select
                                    label="Analysis Depth"
                                    value={generationOptions.analysis_depth}
                                    onChange={(value: string | null) => setGenerationOptions(prev => ({ ...prev, analysis_depth: (value as any) || prev.analysis_depth }))}
                                    data={[
                                        { value: 'SHALLOW', label: '🔍 Shallow - Quick analysis' },
                                        { value: 'STANDARD', label: '🔎 Standard - Balanced analysis' },
                                        { value: 'DEEP', label: '🔬 Deep - Thorough analysis' },
                                        { value: 'COMPREHENSIVE', label: '🧪 Comprehensive - Full analysis' }
                                    ]}
                                />

                                <NumberInput
                                    label="Maximum Recommendations"
                                    value={generationOptions.max_recommendations}
                                    onChange={(value: any) => setGenerationOptions(prev => ({ ...prev, max_recommendations: Number(value) || 5 }))}
                                    min={1}
                                    max={20}
                                />

                                <Select
                                    label="Risk Tolerance"
                                    value={generationOptions.risk_tolerance}
                                    onChange={(value: string | null) => setGenerationOptions(prev => ({ ...prev, risk_tolerance: (value as any) || prev.risk_tolerance }))}
                                    data={[
                                        { value: 'CONSERVATIVE', label: '🛡️ Conservative - Low risk only' },
                                        { value: 'MODERATE', label: '⚖️ Moderate - Balanced risk' },
                                        { value: 'AGGRESSIVE', label: '🚀 Aggressive - Higher risk acceptable' }
                                    ]}
                                />
                            </Stack>
                        </div>
                    </SimpleGrid>

                    <Group justify="flex-end">
                        <Button variant="light" onClick={() => setShowGenerationModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handleGenerateRecommendations(generationOptions)}
                            loading={isGenerating}
                            leftSection={<IconBrain size={16} />}
                        >
                            Generate Recommendations
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* Approval Modal with Life-Critical Checks */}
            <Modal
                opened={showApprovalModal}
                onClose={() => setShowApprovalModal(false)}
                title="Approve Optimization Recommendation"
                size="lg"
            >
                {selectedRecommendation && (
                    <Stack gap="md">
                        <Alert icon={<IconShieldCheck size={16} />} color="blue">
                            <Text fw={500}>Life-Critical Review Required</Text>
                            <Text size="sm">
                                This recommendation affects life-critical workflows. Please review all safety and 
                                compliance implications before approval.
                            </Text>
                        </Alert>

                        <div>
                            <Text fw={500} mb="xs">Recommendation Summary</Text>
                            <Text size="sm" mb="md">{selectedRecommendation.description}</Text>
                            
                            <SimpleGrid cols={2} spacing="md">
                                <div>
                                    <Text fw={500} mb="xs">Safety Assessment</Text>
                                    <Progress
                                        value={selectedRecommendation.safety_validation.safety_score}
                                        color={getSafetyScoreColor(selectedRecommendation.safety_validation.safety_score)}
                                        size="lg"
                                    />
                                </div>
                                <div>
                                    <Text fw={500} mb="xs">Compliance Risk</Text>
                                    <Progress
                                        value={selectedRecommendation.regulatory_impact.compliance_risk_score}
                                        color={selectedRecommendation.regulatory_impact.compliance_risk_score >= complianceThresholds.critical ? 'red' :
                                               selectedRecommendation.regulatory_impact.compliance_risk_score >= complianceThresholds.warning ? 'yellow' : 'green'}
                                        size="lg"
                                    />
                                </div>
                            </SimpleGrid>
                        </div>

                        {selectedRecommendation.regulatory_impact.regulatory_approval_required && (
                            <Alert icon={<IconGavel size={16} />} color="orange">
                                <Text fw={500}>Regulatory Approval Required</Text>
                                <Text size="sm">
                                    This recommendation requires additional regulatory approval before implementation.
                                </Text>
                            </Alert>
                        )}

                        <Textarea
                            label="Approval Notes"
                            placeholder="Add notes about your approval decision..."
                            value={approvalNotes}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setApprovalNotes(e.currentTarget.value)}
                            minRows={3}
                        />

                        <Group justify="flex-end">
                            <Button variant="light" onClick={() => setShowApprovalModal(false)}>
                                Cancel
                            </Button>
                            <Button
                                color="red"
                                variant="light"
                                onClick={() => handleApproval(selectedRecommendation, false)}
                            >
                                Reject
                            </Button>
                            <Button
                                color="green"
                                onClick={() => handleApproval(selectedRecommendation, true)}
                                disabled={selectedRecommendation.safety_validation.safety_score < SAFETY_THRESHOLDS.MINIMUM_SAFETY_SCORE}
                            >
                                Approve Implementation
                            </Button>
                        </Group>
                    </Stack>
                )}
            </Modal>

            {/* Implementation Planning Modal */}
            <Modal
                opened={showImplementationModal}
                onClose={() => setShowImplementationModal(false)}
                title="Plan Implementation"
                size="xl"
            >
                {selectedRecommendation && (
                    <Stack gap="md">
                        <Alert icon={<IconPlayerPlay size={16} />} color="blue">
                            <Text fw={500}>Implementation Planning</Text>
                            <Text size="sm">
                                Plan the implementation phases, rollback procedures, and monitoring strategy for this optimization.
                            </Text>
                        </Alert>

                        {/* Implementation planning form would go here */}
                        <Text c="dimmed" ta="center" py="xl">
                            Implementation planning interface would be implemented here with:
                            <br />• Phase planning and dependencies
                            <br />• Rollback strategy definition
                            <br />• Testing and validation requirements
                            <br />• Monitoring and alerting setup
                            <br />• Risk mitigation measures
                        </Text>

                        <Group justify="flex-end">
                            <Button variant="light" onClick={() => setShowImplementationModal(false)}>
                                Cancel
                            </Button>
                            <Button color="blue">
                                Begin Implementation
                            </Button>
                        </Group>
                    </Stack>
                )}
            </Modal>
        </Container>
    );
};

export default OptimizationRecommendationViewer;